import { PayloadAction } from '@reduxjs/toolkit';
import { AgentFirstCartEditVia } from '../constants/agentFirstCartEdit';
import { CartItemAdditionTypes } from '../constants/event';
import { CartExpandCollapseState } from '../constants/enums';
import {
  CartItem,
  selectModifierInCartItem,
  setModifierQuantityInCartItem,
  trackSuppressUpsellOnSelection,
} from '../utils/cart';
import {
  applyUpsellConditionsToCartItem,
  excludedItemHandler,
  experimentWithheldHandler,
  expressModeHandler,
  suppressUpsellHandler,
  unavailableOptionHandler,
  UpsellConditionHandler,
} from '../utils/cart.upsellConditions';
import logger from '../utils/logger';
import { collectNestedModifierGroups } from '../utils/optionReplacement';
import { canShowBMItem } from '../components/ItemBuilder/menu/modGroup.utils';
import { isBMModifierAnUnavailableConstraint } from '../utils/menuUnification.utils';
import { getParentModifier } from '../utils/menu';
import {
  IBrandUnavailableEntity,
  IRestaurantUnavailableEntity,
} from '../types/brandMenu';
import {
  IBatchedModifierSelection,
  ICartItemsState,
  ISelectModifierPayload,
  ISetModifierQuantityPayload,
} from './cartSlice.props';

/**
 * HITL-1611 — the cart's item reducers, shared verbatim by `cartSlice` and
 * `draftCart`.
 *
 * Every function here was lifted unchanged out of `cartSlice.ts`; the only edit
 * was widening `state` from `ICartState` to `ICartItemsState`, the five-field
 * subset they actually touch. Both slices reference these same functions rather
 * than each holding a copy, so the agent's draft cart cannot drift from the real
 * one as the cart evolves. RTK still generates separate action creators per
 * slice (`cart/addItemToCart` vs `draftCart/addItemToCart`), which is what lets
 * a later agent-origin guard tell the agent's writes from the AI's.
 *
 * The three helpers below stay private: nothing outside these reducers has ever
 * referenced them.
 */

const applyModifierSelection = (
  state: ICartItemsState,
  payload: ISelectModifierPayload
) => {
  const {
    cartItemId,
    menuItemId,
    modGroupId,
    selected,
    modCode,
    modSymbolMapping,
    pathToItem,
    rootModGroupId,
    posSettings,
    readback,
    quantity,
    bmItemsSynonyms,
    applySuppressCascade,
    suppressUpsellEnabled = false,
    applyExpressCascade = false,
    withheldConstraints = [],
  } = payload;
  const { cartItems, modality } = state;
  explicitExpandCartItem(state, cartItemId);
  const cartItem = cartItems[cartItemId];
  const insertedModifier = selectModifierInCartItem(
    cartItem,
    menuItemId,
    modGroupId,
    selected,
    modCode,
    modSymbolMapping,
    posSettings,
    modality,
    pathToItem,
    rootModGroupId,
    readback,
    quantity,
    bmItemsSynonyms
  );
  if (suppressUpsellEnabled) {
    trackSuppressUpsellOnSelection(cartItem, modGroupId, menuItemId, selected);
  }
  // Scope upsell cascades to the just-inserted modifier — only that
  // subtree could surface new prompts. Skip on deselection (nothing
  // added) or when insertion didn't happen (e.g. max-selections hit).
  if (selected && insertedModifier) {
    const cascadeHandlers: UpsellConditionHandler[] = [
      ...(suppressUpsellEnabled && applySuppressCascade
        ? [suppressUpsellHandler]
        : []),
      ...(applyExpressCascade ? [expressModeHandler] : []),
      ...(withheldConstraints.length
        ? [experimentWithheldHandler(withheldConstraints)]
        : []),
      excludedItemHandler,
      unavailableOptionHandler,
    ];
    if (cascadeHandlers.length > 0) {
      applyUpsellConditionsToCartItem(
        insertedModifier,
        modality,
        cascadeHandlers,
        [cartItem.name?.toLowerCase()]
      );
    }
  }
};

const expandCollapseCartItems = function (
  state: ICartItemsState,
  cartItemId: number
) {
  Object.keys(state.cartItemCollapseStates).forEach((id) => {
    const numId = parseInt(id);
    if (
      state.cartItemCollapseStates[numId] ===
      CartExpandCollapseState.AUTO_EXPAND
    ) {
      state.cartItemCollapseStates[numId] =
        CartExpandCollapseState.AUTO_COLLAPSE;
    }
  });
  state.cartItemCollapseStates[cartItemId] =
    CartExpandCollapseState.AUTO_EXPAND;
};

const explicitExpandCartItem = function (
  state: ICartItemsState,
  cartItemId: number
) {
  const currentState = state.cartItemCollapseStates[cartItemId];
  if (
    currentState === CartExpandCollapseState.MANUAL_COLLAPSE ||
    currentState === CartExpandCollapseState.AUTO_COLLAPSE
  ) {
    state.cartItemCollapseStates[cartItemId] =
      CartExpandCollapseState.AUTO_EXPAND;
  }
};

export const addItemToCart = (
  state: ICartItemsState,
  action: PayloadAction<{
    cartItem: CartItem;
    suppressUpsellsInExpressMode?: boolean;
    suppressUpsellEnabled?: boolean;
    withheldConstraints?: string[];
    /**
     * HITL-1646 — observability only, and READ BY NOTHING IN THIS REDUCER.
     *
     * Three journeys end in this action on the draft cart — Copy all, a
     * per-item copy and an omnibar add — and telling them apart is the whole
     * point of the `agent_first_cart_edit` metric's `via`. The two copy paths
     * say which they are here; the omnibar is the untagged residual. See
     * `constants/agentFirstCartEdit`.
     *
     * It rides on the payload rather than being inferred, because inference
     * would have to key on `addedBy` or on dispatch batching, and neither
     * survives a new caller.
     */
    via?: AgentFirstCartEditVia;
  }>
) => {
  const {
    cartItem,
    suppressUpsellsInExpressMode = false,
    suppressUpsellEnabled = false,
    withheldConstraints = [],
  } = action.payload;
  const cartItemId = cartItem.cartItemId;
  state.cartItems[cartItemId] = cartItem;
  state.cartItemsQuantity[cartItemId] = 1;
  state.sequenceId++;
  expandCollapseCartItems(state, cartItemId);
  // Skip upsell auto-decline for AI/hypothesis-driven additions — they
  // replay an already-resolved order and shouldn't trigger session-level effects.
  if (cartItem.addedBy !== CartItemAdditionTypes.AI) {
    const handlers: UpsellConditionHandler[] = [
      ...(suppressUpsellEnabled ? [suppressUpsellHandler] : []),
      ...(suppressUpsellsInExpressMode ? [expressModeHandler] : []),
      ...(withheldConstraints.length
        ? [experimentWithheldHandler(withheldConstraints)]
        : []),
      excludedItemHandler,
      unavailableOptionHandler,
    ];
    applyUpsellConditionsToCartItem(
      state.cartItems[cartItemId],
      state.modality,
      handlers
    );
  }
};

// Declines the withheld groups of items the agent added before the car's assignment arrived.
export const declineWithheldGroups = (
  state: ICartItemsState,
  {
    payload: { withheldConstraints },
  }: PayloadAction<{ withheldConstraints: string[] }>
) => {
  if (!withheldConstraints.length) return;
  const handlers = [experimentWithheldHandler(withheldConstraints)];
  Object.values(state.cartItems).forEach((cartItem) => {
    if (cartItem.addedBy === CartItemAdditionTypes.AI) return;
    applyUpsellConditionsToCartItem(cartItem, state.modality, handlers);
  });
};

export const selectModifier = (
  state: ICartItemsState,
  { payload }: PayloadAction<ISelectModifierPayload>
) => {
  applyModifierSelection(state, payload);
};

// HITL-1756: the agent's count on a selected modifier ("4 cream"). Sets, never
// adds; see setModifierQuantityInCartItem.
export const setModifierQuantity = (
  state: ICartItemsState,
  { payload }: PayloadAction<ISetModifierQuantityPayload>
) => {
  const { cartItemId, menuItemId, modGroupId, quantity, pathToItem } = payload;
  const cartItem = state.cartItems[cartItemId];
  if (!cartItem) {
    logger.warn(
      `Not setting quantity of ${menuItemId} in group ${modGroupId}: cart item ${cartItemId} no longer exists`
    );
    return;
  }
  explicitExpandCartItem(state, cartItemId);
  const updated = setModifierQuantityInCartItem(
    cartItem,
    menuItemId,
    modGroupId,
    quantity,
    pathToItem,
    payload.rootModGroupId
  );
  if (!updated) {
    logger.warn(
      `Not setting quantity of ${menuItemId} in group ${modGroupId} (path ${pathToItem}): it is not selected or takes no count`
    );
  }
};

// Applies a whole tree of modifier selections in a single dispatch.
// Sequential per-modifier dispatches on items with large constraint sets
// (e.g. Crowd Pleasers) each trigger a full store notification + React
// render pass, which is what lagged HITL during hypothesis replays.
export const selectModifiers = (
  state: ICartItemsState,
  {
    payload: { selections, unavailableItemsByName },
  }: PayloadAction<{
    selections: IBatchedModifierSelection[];
    unavailableItemsByName?: Record<
      string,
      Array<IRestaurantUnavailableEntity | IBrandUnavailableEntity>
    >;
  }>
) => {
  const applyBatch = (batch: IBatchedModifierSelection[]) => {
    batch.forEach(({ selection, unavailableConstraintCheck, children }) => {
      const cartItem = state.cartItems[selection.cartItemId];
      // Batched selections can be applied a microtask after they were
      // queued (default-selection flush), so the target item may have
      // been deleted in between — skip rather than crash.
      if (!cartItem) {
        logger.warn(
          `Skipping batched selection of ${selection.menuItemId} in group ${selection.modGroupId} (path ${selection.pathToItem}): cart item ${selection.cartItemId} no longer exists`
        );
        return;
      }
      if (unavailableConstraintCheck && unavailableItemsByName) {
        const { parentModifier, modGroupName, itemName } =
          unavailableConstraintCheck;
        if (
          isBMModifierAnUnavailableConstraint(
            cartItem,
            unavailableItemsByName,
            parentModifier,
            selection.pathToItem || '',
            modGroupName,
            itemName
          )
        ) {
          logger.warn(
            `Not adding to cart as the item ${itemName} is unavailable`
          );
          return;
        }
      }
      applyModifierSelection(state, selection);
      if (children?.length) {
        applyBatch(children);
      }
    });
  };
  applyBatch(selections);
};

/**
 * HITL-1617 — moved here from `cartSlice.ts` verbatim, so `draftCart` has it too.
 *
 * The plan listed this one as "arrives with the write-path routing", and it has
 * to: the centre pane's option picks run through `useItemConversion`, which
 * dispatches `applyOptionReplacement` whenever a business rule fires (a combo
 * size change re-picking the dependent side and drink sizes). Left on
 * `cartSlice` alone, a size pick on a My Cart line would reach across and
 * rewrite the AI's item — HITL-1606's invariant broken by the one write the agent
 * never asked for.
 *
 * The only edit is `state: ICartState` -> `ICartItemsState`; it touches
 * `cartItems` and `modality`, both of which the draft has.
 */
export const applyOptionReplacement = (
  state: ICartItemsState,
  {
    payload: { cartItemId, targetModifierGroups, unavailableItemsByName },
  }: PayloadAction<{
    cartItemId: number;
    targetModifierGroups: Array<{
      modifierGroupName: string;
      optionName: string;
    }>;
    unavailableItemsByName: Record<
      string,
      Array<IRestaurantUnavailableEntity | IBrandUnavailableEntity>
    >;
  }>
) => {
  const { cartItems } = state;
  const cartItem = cartItems[cartItemId];

  if (!cartItem) {
    logger.debug({
      message: `Can't apply option replacement due to cart item ${cartItemId} not found for option replacement`,
    });
    return;
  }

  // Apply option replacement to each target modifier group
  const nestedGroups = collectNestedModifierGroups(cartItem);
  targetModifierGroups.forEach(({ modifierGroupName, optionName }) => {
    const modifierGroup =
      Object.values(cartItem.modifierGroups || {}).find(
        (group) => group.name === modifierGroupName
      ) ?? nestedGroups.get(modifierGroupName)?.modifierGroup;

    const canShowItem = canShowBMItem({
      unavailableItemsByName,
      itemName: optionName,
      cartItem,
      modGroupName: modifierGroupName,
    });

    if (modifierGroup && canShowItem) {
      // Find the menu item that matches the option name
      const menuItem = Object.values(modifierGroup.menuItems || {}).find(
        (item) => item.name === optionName
      );

      // HITL-1708: `canShowBMItem` only drops a NOT-SOLD option. A default-size
      // rule's `target_options` reach here unchecked, so an option 86'd today,
      // meal-period inactive, or unavailable as this choice must be refused
      // here too — the same two checks the size-match targets get upstream
      // (optionReplacement.ts) and the agent's own pick gets in the picker.
      const isUnavailable =
        !!menuItem &&
        (menuItem.available === false ||
          isBMModifierAnUnavailableConstraint(
            cartItem,
            unavailableItemsByName,
            getParentModifier(menuItem.pathToItem || ''),
            menuItem.pathToItem || '',
            modifierGroup.name,
            menuItem.name || menuItem.itemId
          ));
      if (isUnavailable) {
        logger.debug({
          message: `Skipping unavailable option replacement target ${optionName} in ${modifierGroupName} for cart item ${cartItem.name}`,
        });
      } else if (menuItem) {
        // Select the modifier — selectModifierInCartItem handles nesting
        // via recursivelyFindSelectedItemNode
        selectModifierInCartItem(
          cartItem,
          menuItem.itemId,
          modifierGroup.id,
          true, // selected
          '', // modCode
          {}, // modSymbolMapping
          {}, // posSettings
          state.modality,
          '', // pathToItem
          '', // rootModGroupId
          true, // readback
          undefined, // quantity - don't pass quantity to avoid adding to existing
          {} // bmItemsSynonyms
        );
      }
    }
  });
};

export const cloneCartItem = (
  state: ICartItemsState,
  { payload }: PayloadAction<{ id: string; quantity: number }>
) => {
  const { id, quantity } = payload;
  state.sequenceId += 1;
  const cartItems = {
    ...state.cartItems,
    [state.sequenceId]: {
      ...state.cartItems[id],
      cartItemId: state.sequenceId,
      isConverted: false, // Duplicated items should not have the converted flag
    },
  };
  expandCollapseCartItems(state, state.sequenceId);
  state.cartItems = { ...cartItems };
  state.cartItemsQuantity[state.sequenceId] = quantity;
  state.sequenceId += 1;
};

export const deleteCartItem = (
  state: ICartItemsState,
  { payload }: PayloadAction<string>
) => {
  delete state.cartItems[payload];
  delete state.cartItemsQuantity[payload];
  delete state.cartItemCollapseStates[parseInt(payload)];
};

export const updateQuantity = (
  state: ICartItemsState,
  { payload }: PayloadAction<{ cartItemId: string; quantity: number }>
) => {
  if (payload.quantity > 0) {
    if (state.cartItemsQuantity[payload.cartItemId] === payload.quantity) {
      // For brand menu auto-combo non-combined item, we will still send the same quantity
      state.cartItemsQuantity = {
        ...state.cartItemsQuantity,
        [payload.cartItemId]: payload.quantity,
      };
    } else {
      state.cartItemsQuantity[payload.cartItemId] = payload.quantity;
    }
  } else {
    delete state.cartItems[payload.cartItemId];
    delete state.cartItemsQuantity[payload.cartItemId];
  }
};

export const setCartItemCollapseState = (
  state: ICartItemsState,
  { payload }: PayloadAction<{ cartItemId: number }>
) => {
  const { cartItemId } = payload;
  const currentState = state.cartItemCollapseStates[cartItemId];
  let newState = CartExpandCollapseState.MANUAL_EXPAND;
  if (
    currentState === CartExpandCollapseState.AUTO_EXPAND ||
    currentState === CartExpandCollapseState.MANUAL_EXPAND
  ) {
    newState = CartExpandCollapseState.MANUAL_COLLAPSE;
  }
  state.cartItemCollapseStates[cartItemId] = newState;
};

export const setExplicitExpandState = (
  state: ICartItemsState,
  { payload }: PayloadAction<{ cartItemId: number }>
) => {
  const { cartItemId } = payload;
  explicitExpandCartItem(state, cartItemId);
};
